import { KeyboardHelper } from '#pplx/frontend/components/utils/keyboardHelpers.js';
import { useCallback, useRef } from 'react';
export function useSuggestDropdownKeyDown({ userInputQuery, userInputJson, suggestionsDisabled, suggestions, focusedIndex, setFocusedIndex, onChange, handleSuggestionSubmission, placement = 'bottom', inputRef, }) {
    const userInputQueryRef = useRef(userInputQuery);
    const userInputJsonRef = useRef(userInputJson);
    const suggestionsRef = useRef(suggestions);
    const focusedIndexRef = useRef(focusedIndex);
    userInputQueryRef.current = userInputQuery;
    userInputJsonRef.current = userInputJson;
    suggestionsRef.current = suggestions;
    focusedIndexRef.current = focusedIndex;
    // This keydown handler is all refs so that we don't need to make new references. But it
    // also means that Proxy traps are not trigger. Iterate the suggestions to ensure that
    // the Proxy knows the array is in use--and updates it--before the arrow key event access
    // triggers the Proxy trap and update instead.
    suggestions.forEach((_) => _);
    const handleArrowDownBottomPlacement = useCallback((e, suggestionsLength) => {
        if (suggestionsLength > 0) {
            const nextIndex = (focusedIndexRef.current + 1) % suggestionsLength;
            const nextSuggestion = suggestionsRef.current[nextIndex];
            if (nextSuggestion?.query) {
                const newInput = nextSuggestion.submission_query ?? nextSuggestion.query;
                onChange(newInput);
            }
            // Prevent default arrow key behavior — navigate suggestion dropdown
            e.preventDefault();
            return nextIndex;
        }
        return null;
    }, [onChange]);
    const handleArrowUpBottomPlacement = useCallback((e) => {
        if (focusedIndexRef.current === -1) {
            // Do not consume the first ArrowUp before any bottom-placement suggestion is selected.
            return null;
        }
        let nextIndex = focusedIndexRef.current - 1;
        if (nextIndex < 0) {
            nextIndex = -1;
            if (focusedIndexRef.current === 0) {
                // Prevent default arrow key behavior — navigate suggestion dropdown
                e.preventDefault();
                onChange(userInputQueryRef.current, userInputJsonRef.current);
            }
        }
        else {
            // Prevent default arrow key behavior — navigate suggestion dropdown
            e.preventDefault();
            const nextSuggestion = suggestionsRef.current[nextIndex];
            if (nextSuggestion?.query) {
                const newInput = nextSuggestion.submission_query ?? nextSuggestion.query;
                onChange(newInput);
            }
        }
        return nextIndex;
    }, [onChange]);
    const handleArrowUpTopPlacement = useCallback((e, suggestionsLength) => {
        if (suggestionsLength > 0) {
            const nextIndex = focusedIndexRef.current <= 0
                ? suggestionsLength - 1
                : (focusedIndexRef.current - 1) % suggestionsLength;
            const nextSuggestion = suggestionsRef.current[nextIndex];
            if (nextSuggestion?.query) {
                const newInput = nextSuggestion.submission_query ?? nextSuggestion.query;
                onChange(newInput);
            }
            // Prevent default arrow key behavior — navigate suggestion dropdown
            e.preventDefault();
            return nextIndex;
        }
        return null;
    }, [onChange]);
    const handleArrowDownTopPlacement = useCallback((e, suggestionsLength) => {
        let nextIndex = focusedIndexRef.current + 1;
        if (nextIndex >= suggestionsLength || nextIndex === 0) {
            nextIndex = -1;
            if (focusedIndexRef.current === suggestionsLength - 1) {
                // Prevent default arrow key behavior — navigate suggestion dropdown
                e.preventDefault();
                onChange(userInputQueryRef.current, userInputJsonRef.current);
            }
        }
        else {
            // Prevent default arrow key behavior — navigate suggestion dropdown
            e.preventDefault();
            const nextSuggestion = suggestionsRef.current[nextIndex];
            if (nextSuggestion?.query) {
                const newInput = nextSuggestion.submission_query ?? nextSuggestion.query;
                onChange(newInput);
            }
        }
        return nextIndex;
    }, [onChange]);
    const handleSuggestDropdownNavigation = useCallback((e) => {
        if (suggestionsDisabled)
            return false;
        const isArrowDown = KeyboardHelper.isArrowDown(e);
        const isArrowUp = KeyboardHelper.isArrowUp(e);
        if (isArrowDown) {
            if (placement === 'bottom' &&
                focusedIndexRef.current === -1 &&
                !inputRef.current?.inLine('last')) {
                return false;
            }
            const maybeNextFocusedIndex = placement === 'bottom'
                ? handleArrowDownBottomPlacement(e, suggestionsRef.current.length)
                : handleArrowDownTopPlacement(e, suggestionsRef.current.length);
            if (maybeNextFocusedIndex !== null) {
                setFocusedIndex(maybeNextFocusedIndex);
                return true;
            }
        }
        else if (isArrowUp) {
            if (placement === 'top' &&
                focusedIndexRef.current === -1 &&
                !inputRef.current?.inLine('first')) {
                return false;
            }
            // For top placement: start from last item (natural bottom-to-top navigation)
            const maybeNextFocusedIndex = placement === 'bottom'
                ? handleArrowUpBottomPlacement(e)
                : handleArrowUpTopPlacement(e, suggestionsRef.current.length);
            if (maybeNextFocusedIndex !== null) {
                setFocusedIndex(maybeNextFocusedIndex);
                return true;
            }
        }
        else if (KeyboardHelper.isEnterKeyWithoutShift(e)) {
            if (focusedIndexRef.current >= 0 &&
                focusedIndexRef.current < suggestionsRef.current.length) {
                const suggestion = suggestionsRef.current[focusedIndexRef.current];
                if (suggestion) {
                    handleSuggestionSubmission(suggestion, focusedIndexRef.current);
                    return true;
                }
            }
        }
        return false;
    }, [
        suggestionsDisabled,
        placement,
        inputRef,
        handleArrowDownBottomPlacement,
        handleArrowDownTopPlacement,
        setFocusedIndex,
        handleArrowUpBottomPlacement,
        handleArrowUpTopPlacement,
        handleSuggestionSubmission,
    ]);
    return handleSuggestDropdownNavigation;
}
//# sourceMappingURL=useSuggestDropdownKeyDown.js.map